import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Prompt } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import { Tabs as AntTabs, TabsProps as AntTabsProps } from 'antd';
import { Tab } from '@models/classes';
import { CommonSelectors } from '@store/selectors';
import { ProjectCategory } from '../project-category';
import { getProjectCategoriesVisibility } from './models/constants';
import { TabsProps } from './models/interfaces';

export const Tabs: FC<TabsProps> = ({
  tabs,
  params,
  destroyInactiveTabPane = false,
  animated = true,
  isLeasingDetails,
}: PropsWithChildren<TabsProps>) => {
  const location = useLocation();
  const history = useHistory();

  const currentTab = new URLSearchParams(location.search).get('tabName')!;

  const locationNameWithTab = currentTab
    ? `${location.pathname}/${currentTab.toLowerCase()}`
    : location.pathname;

  const hasUnsubmittedData = useSelector(CommonSelectors.getHasUnsubmittedData);

  const changeTab = (tabName: string): void => {
    const selectedTab = tabs.find((tab: Tab) => tab.name === tabName);

    if (selectedTab?.isLink) {
      history.replace(selectedTab.replacePath(params));
    } else {
      const searchParams = new URLSearchParams();

      searchParams.set('tabName', tabName);

      history.replace(`${location.pathname}?${searchParams}`);
    }
  };

  useEffect(() => {
    if (!tabs.find((tab: Tab) => tab.name === currentTab)) {
      const [firstTab] = tabs;

      changeTab(firstTab.name);
    }
  }, [currentTab, tabs]);

  const items: AntTabsProps['items'] = tabs.map((tab: Tab) => ({
    key: tab.name,
    label: tab.name,
    children: (
      <>
        {getProjectCategoriesVisibility(locationNameWithTab) ? (
          <ProjectCategory isLeasingDetails={isLeasingDetails} />
        ) : null}
        <Prompt when={hasUnsubmittedData} message="" />
        {React.createElement(tab.component, {
          isActive: currentTab === tab.name,
          history,
          params,
          ...tab.componentProps,
        })}
      </>
    ),
  }));

  return (
    <AntTabs
      animated={animated}
      activeKey={currentTab}
      onChange={changeTab}
      destroyInactiveTabPane={destroyInactiveTabPane}
      items={items}
    />
  );
};
